import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { CognitoService } from 'src/app/modules/shared/services/cognito.service';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private auth: CognitoService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {

    const isApiRequest =
      req.url.startsWith(this.auth.appUri) ||
      req.url.startsWith(this.auth.appBotanicUri);

    if (!isApiRequest) {
      return next.handle(req);
    }

    const sendRequest = (token: string | null) => {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', token ? token.toString() : '')
      });

      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {

          // If API returns 401 → try refresh once
          if (error.status === 401 && this.auth.isAuthenticated) {

            return from(this.auth.refreshSession()).pipe(
              switchMap(() => {

                const retryReq = req.clone({
                  headers: req.headers.set(
                    'Authorization',
                    this.auth.TokenId ? this.auth.TokenId.toString() : ''
                  )
                });

                return next.handle(retryReq);
              }),
              catchError(() => {
                this.auth.signOut();
                this.router.navigate(['/login']);
                return throwError(() => error);
              })
            );
          }

          return throwError(() => error);
        })
      );
    };

    // Refresh token before request if expired.
    // If refresh itself fails (e.g. refresh token revoked/expired), sign out and redirect to login.
    if (this.auth.isTokenExpired() && this.auth.isAuthenticated) {
      return from(this.auth.refreshSession()).pipe(
        switchMap(() => sendRequest(this.auth.TokenId)),
        catchError(() => {
          this.auth.signOut();
          this.auth.clearSession();
          this.router.navigate(['/login']);
          // Propagate the error so components can run cleanup (e.g. hide loaders)
          return throwError(() => new Error('Session expired'));
        })
      );
    }

    return sendRequest(this.auth.TokenId);
  }
}
